import { createFileRoute, notFound, redirect, useRouter } from "@tanstack/react-router";
import { YearPage } from "@/components/pages/YearPage";
import { SiteShell } from "@/components/layout/SiteShell";
import {
  parseYearSlug,
  parseMonthSlug,
  parseDateSlug,
  MONTH_SLUGS_EN_LIST,
} from "@/lib/calendar/dates";
import { translatePathToId } from "@/i18n/paths";
import { withSlash } from "@/lib/url";

export const Route = createFileRoute("/en/calendar/$year/")({
  loader: ({ params }) => {
    const y = parseYearSlug(params.year);
    if (y === null) {
      // Legacy flat slug (EN or ID month token) → 308 to canonical EN nested form.
      const idForm = translatePathToId(`/${params.year}`).slice(1);
      const ym = parseMonthSlug(idForm);
      if (ym) {
        const to = `/en/calendar/${ym.y}/${MONTH_SLUGS_EN_LIST[ym.m - 1]}/`;
        if (to !== `/en/calendar/${params.year}/`) {
          throw redirect({ to, code: 308 } as any);
        }
      }
      const ymd = parseDateSlug(idForm);
      if (ymd) {
        const to = `/en/calendar/${ymd.y}/${ymd.d}-${MONTH_SLUGS_EN_LIST[ymd.m - 1]}/`;
        if (to !== `/en/calendar/${params.year}/`) {
          throw redirect({ to, code: 308 } as any);
        }
      }
      throw notFound();
    }
    return { y };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    const title = `Indonesia Calendar ${loaderData.y} - Public Holidays & Joint Leave`;
    const desc = `Indonesia calendar ${loaderData.y} with public holidays, joint leave, Hijri, Javanese & Balinese.`;
    const canonical = `https://hari.co.id${withSlash(`/en/calendar/${params.year}`)}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "id_ID" },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  component: EnCalendarYear,
  notFoundComponent: () => (
    <SiteShell locale="en">
      <div className="py-16 text-center"><h1 className="text-2xl font-bold">Page not found</h1></div>
    </SiteShell>
  ),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <SiteShell locale="en">
        <div className="py-16 text-center">
          <h1 className="text-2xl font-bold">Something went wrong</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">
            Retry
          </button>
        </div>
      </SiteShell>
    );
  },
});

function EnCalendarYear() {
  const { y } = Route.useLoaderData();
  return <YearPage y={y} locale="en" />;
}